<template>
    <span
        :title="formattedLabel"
        class="event-date"
    >
        {{ formattedLabel }}
    </span>
</template>

<script setup lang="ts">
import {
    parseISO,
    isToday,
    isTomorrow,
    isSameWeek,
    isThisYear,
    endOfToday,
    format,
    getDay
} from 'date-fns';

import { timeFormat } from '~/plugins/dates';

const props = defineProps<{
    startDate: string;
    endDate?: string | null;
}>();

const { startDate, endDate } = toRefs(props);

const startDateObj = computed(() => {
    return parseISO(startDate.value);
});

const endDateObj = computed(() => {
    if (!endDate.value) {
        return;
    }

    return parseISO(endDate.value);
});

const startLabel = computed(() => {
    // 1. Only start date: today
    if (isToday(startDateObj.value)) {
        return `Vandaag om ${format(startDateObj.value, timeFormat)}`;
    }

    // 2. Only start date: tomorrow
    if (isTomorrow(startDateObj.value)) {
        return `Morgen om ${format(startDateObj.value, timeFormat)}`;
    }

    // 3. Somewhere this week
    const thisWeek = isSameWeek(startDateObj.value, {
        weekStartsOn: getDay(endOfToday())
    });

    if (thisWeek) {
        return format(startDateObj.value, `EEEE 'om' ${timeFormat}`);
    }

    // Next year
    if (!isThisYear(startDateObj.value)) {
        return `${format(startDateObj.value, `EEEE d MMM yyyy 'om' ${timeFormat}`)}`;
    }

    // 5. Somewhere in the future (or past)
    return format(startDateObj.value, `EEEE d MMM 'om' ${timeFormat}`);
});

const startEndLabel = computed(() => {
    if (!endDateObj.value) {
        return;
    }

    // 1.1. Today, with end time
    if (isToday(startDateObj.value) && isToday(endDateObj.value)) {
        return `Vandaag van ${format(startDateObj.value, timeFormat)} tot ${format(endDateObj.value, timeFormat)}`;
    }

    // 1.2. Today, until tomorrow
    if (isToday(startDateObj.value) && isTomorrow(endDateObj.value)) {
        return `Vandaag van ${format(startDateObj.value, timeFormat)} tot morgen ${format(endDateObj.value, timeFormat)}`;
    }

    // 1.3. Today, until later this week
    const isWithinOneWeekOfToday = isSameWeek(endDateObj.value, {
        weekStartsOn: getDay(endOfToday())
    });

    if (isToday(startDateObj.value) && isWithinOneWeekOfToday) {
        return `Vandaag van ${format(startDateObj.value, timeFormat)} tot ${format(endDateObj.value, `EEEE 'om' ${timeFormat}`)}`;
    }

    // 1.4. Today, until after next week
    if (isToday(startDateObj.value)) {
        return `Vandaag van ${format(startDateObj.value, timeFormat)} tot ${format(endDateObj.value, `EEEE d MMM 'om' ${timeFormat}`)}`;
    }

    // 2.1. Tomorrow, with end time
    if (isTomorrow(startDateObj.value) && isTomorrow(endDateObj.value)) {
        return `Morgen van ${format(startDateObj.value, timeFormat)} tot ${format(endDateObj.value, timeFormat)}`;
    }

    // 2.2. Tomorrow - later this week
    const isWithinOneWeekOfTomorrow = isSameWeek(endDateObj.value, {
        weekStartsOn: getDay(endOfToday())
    });

    if (isTomorrow(startDateObj.value) && isWithinOneWeekOfTomorrow) {
        return `Morgen van ${format(startDateObj.value, timeFormat)} tot ${format(endDateObj.value, `EEEE ${timeFormat}`)}`;
    }

    // 2.3. Tomorrow - past next week
    if (isTomorrow(startDateObj.value)) {
        return `Morgen van ${format(startDateObj.value, timeFormat)} tot ${format(endDateObj.value, `EEEE d MMM ${timeFormat}`)}`;
    }

    // 3.1. Next year
    if (!isThisYear(endDateObj.value)) {
        return `${format(startDateObj.value, `EEEE d MMM ${timeFormat}`)} tot ${format(endDateObj.value, `dd MMM yyyy 'om' ${timeFormat}`)}`;
    }

    // 4. Somewhere in the future (or past)
    return `${format(startDateObj.value, `EEEE d MMM ${timeFormat}`)} tot ${format(endDateObj.value, `EEEE d MMM ${timeFormat}`)}`;
});

const formattedLabel = computed(() => {
    let res: string;

    if (startEndLabel.value) {
        res = startEndLabel.value;
    } else {
        res = startLabel.value;
    }

    res = res[0].toUpperCase() + res.substring(1);

    return res;
});

</script>

<style src="./EventDate.less" lang="less"></style>
